import { createTheme, alpha } from "@mui/material/styles";
import "@mui/x-date-pickers/themeAugmentation";
import AppConfig from "@app/util/AppConfig";
// ─── 2. Design tokens ────────────────────────────────────────────────────────
// Tailwind v4 palette — only the families/shades this theme actually references.
// (`primary` is the one brand color that intentionally stays off this palette.)
const tw = {
    white: "#ffffff",
    black: "#000000",
    neutral: { 50: "#fafafa", 100: "#f5f5f5", 200: "#e5e5e5", 300: "#d4d4d4", 400: "#a3a3a3", 500: "#737373", 600: "#525252", 700: "#404040", 800: "#262626", 900: "#171717", 950: "#0a0a0a" },
    gray: { 400: "#9ca3af" },
    slate: { 400: "#94a3b8", 500: "#64748b", 600: "#475569" },
    rose: { 400: "#fb7185", 500: "#f43f5e", 700: "#be123c" }, // kept defined for other/future use — error uses `red` below
    red: { 400: "#f87171", 500: "#ef4444", 700: "#b91c1c" },
    orange: { 500: "#f97316", 600: "#ea580c" },
    yellow: { 500: "#eab308" },
    green: { 400: "#26c87f", 500: "#15ce6f", 600: "#0db66c", 700: "#0a9e5f" },
    emerald: { 400: "#34d399", 500: "#10b981", 600: "#059669" },
    teal: { 600: "#0d9488" },
    cyan: { 600: "#0891b2" },
    sky: { 300: "#7dd3fc", 500: "#0ea5e9", 600: "#0284c7" },
    indigo: { 600: "#4f46e5" },
    purple: { 500: "#a855f7" },
    pink: { 600: "#db2777" },
    // Custom
    error: { 400: "#f2466b", 500: "#e71a46", 700: "#e71a46" },
};
// Named accent swatch: main + soft tint/border/hover derivations.
const named = (main) => ({ main, light: alpha(main, 0.15), border: alpha(main, 0.3), hover: alpha(main, 0.5) });
const getDesignTokens = (mode) => {
    const light = mode === "light";
    // Chip / tag / level tint ramp — mode-aware fg + translucent bg, sourced from `tw`.
    const tone = light
        ? {
            primary: { fg: tw.sky[600], bg: alpha(tw.sky[500], 0.13) },
            success: { fg: tw.green[600], bg: alpha(tw.green[500], 0.15) },
            warning: { fg: tw.orange[500], bg: alpha(tw.yellow[500], 0.12) },
            error: { fg: tw.error[700], bg: alpha(tw.error[500], 0.12) },
            slate: { fg: tw.slate[600], bg: alpha(tw.slate[500], 0.14) },
            neutral: { fg: tw.neutral[400], bg: alpha(tw.neutral[500], 0.12) },
        }
        : {
            primary: { fg: tw.sky[300], bg: alpha(tw.sky[500], 0.20) },
            success: { fg: tw.green[400], bg: alpha(tw.green[500], 0.18) },
            warning: { fg: tw.yellow[500], bg: alpha(tw.yellow[500], 0.18) },
            error: { fg: tw.error[400], bg: alpha(tw.error[500], 0.22) },
            slate: { fg: tw.slate[400], bg: alpha(tw.slate[500], 0.22) },
            neutral: { fg: tw.neutral[400], bg: alpha(tw.white, 0.08) },
        };
    // Surface / foreground / border scale (formerly a nested `appColors` object;
    // now flattened directly onto `palette` below — computed once here so
    // text/background/divider can reuse the same values).
    const pageBg = light ? tw.neutral[100] : tw.neutral[950];
    const cardBg = light ? tw.white : tw.neutral[900];
    const cardBgAlt = light ? tw.neutral[100] : tw.neutral[800]; // muted alt surface (zebra / hover / header)
    const input = light ? tw.white : tw.neutral[900]; // input / select / textarea / chip / tag / level
    const raised = light ? tw.white : tw.neutral[900]; // menus, popovers, raised sheets
    const headerTint = light ? tw.neutral[50] : tw.neutral[800];
    const rowHover = light ? alpha(tw.black, 0.025) : alpha(tw.white, 0.04);
    const primaryFg = light ? tw.neutral[900] : tw.white;
    const secondaryFg = light ? tw.neutral[600] : tw.neutral[300];
    const tertiaryFg = tw.neutral[400];
    const quaternaryFg = light ? tw.neutral[100] : tw.neutral[600];
    const border = light ? alpha(tw.neutral[300], 0.5) : alpha(tw.white, 0.10);
    const borderStrong = light ? tw.neutral[300] : tw.neutral[700]; // more visible — input outlines
    const borderSubtle = light ? tw.neutral[200] : alpha(tw.white, 0.06); // faint — dividers
    const invBg = light ? tw.neutral[900] : tw.neutral[100]; // inverted surface (tooltips)
    const invFg = light ? tw.white : tw.neutral[900];
    const palette = {
        mode,
        primary: {
            main: "#00A9DF", dark: "#008bb7",
            light: alpha("#00A9DF", 0.13),
            border: alpha("#00A9DF", 0.3), hover: alpha("#00A9DF", 0.5),
        },
        secondary: {
            // subtle neutral fill (segmented tracks, quiet buttons)
            main: light ? tw.neutral[100] : tw.neutral[800],
            dark: light ? tw.neutral[200] : tw.neutral[700],
            contrastText: light ? tw.neutral[600] : tw.white,
        },
        error: { main: tw.error[500], dark: light ? tw.error[700] : tw.error[400], light: alpha(tw.error[500], light ? 0.12 : 0.1) },
        warning: { main: tw.orange[500], contrastText: tw.orange[600], light: alpha(tw.orange[500], light ? 0.12 : 0.1) },
        success: { main: tw.green[600], dark: light ? tw.green[700] : tw.green[700], light: alpha(tw.green[500], light ? 0.15 : 0.12) },
        info: { main: tw.sky[500], dark: light ? tw.sky[600] : tw.sky[400], light: alpha(tw.sky[500], 0.13) },
        text: { primary: primaryFg, secondary: secondaryFg, tertiary: tertiaryFg, quaternary: quaternaryFg },
        common: { black: tw.black, white: tw.white },
        background: { default: pageBg, paper: cardBg },
        divider: border,
        // Surface / border tokens (formerly `appColors`). Foreground/text tokens live
        // under `text.*` above instead — see TypeText augmentation for `quaternary`.
        pageBg, cardBg, cardBgAlt, raised, headerTint, rowHover,
        border, borderStrong, borderSubtle, invBg, invFg,
        input,
        // Named accent colors (comp-audit outcomes + app-wide use). Each: main/light/border/hover.
        gray: named(tw.gray[400]),
        purple: named(tw.purple[500]),
        yellow: named(tw.yellow[500]),
        orange: named(tw.orange[500]),
        teal: named(tw.teal[600]),
        green: named(tw.green[600]),
        emerald: named(tw.emerald[500]),
        slate: named(tw.slate[500]),
        rose: named(tw.rose[500]),
        red: named(tw.red[500]),
        indigo: named(tw.indigo[600]),
        pink: named(tw.pink[600]),
        cyan: named(tw.cyan[600]),
        sky: named(tw.sky[600]),
    };
    return { palette, tone };
};
// Soft shadow ramp (kit uses low-opacity, short-throw shadows). 25 entries required by MUI.
const softShadows = (() => {
    const specs = [
        "0px 2px 4px rgba(0,0,0,0.05)",
        "0px 2px 8px rgba(0,0,0,0.08)",
        "0px 4px 12px rgba(0,0,0,0.10)",
        "0px 6px 16px rgba(0,0,0,0.12)",
        "0px 8px 20px rgba(0,0,0,0.14)",
        "0px 12px 28px rgba(0,0,0,0.16)",
        "0px 16px 48px rgba(0,0,0,0.18)",
    ];
    const out = ["none"];
    for (let i = 1; i < 25; i++)
        out.push(specs[Math.min(i - 1, specs.length - 1)]);
    return out;
})();
// ─── 3. Theme + components ─────────────────────────────────────────────────────
const createThemeWithComponents = (mode) => {
    const { palette, tone } = getDesignTokens(mode);
    const dark = mode === "dark";
    // Shared style fragments reused across multiple component overrides below.
    const hairlineBorder = `1px solid ${palette.border}`;
    const hoverShadowSm = "0px 4px 8px rgba(0,0,0,0.12)";
    const hoverShadowMd = "0px 6px 12px rgba(0,0,0,0.15)";
    const selectedPickerBase = {
        backgroundColor: `${palette.primary.main} !important`,
        color: `${palette.common.white} !important`,
    };
    // MuiAlert colors, matching BoxFormAlertBar's design language: a solid `${severity}.light`
    // tint and a tinted rounded icon box. Single-line alerts read fully colored (icon + message
    // both `main`) -- titled (title + description) alerts instead drop the body to plain
    // text.primary and reserve color for the icon + title, through `dark` (deeper, more legible
    // at a glance than `main`). Warning has no `dark` token, so it uses `contrastText` instead
    // (also why BoxFormAlertBar's SEVERITY_TEXT_COLOR bypasses `main` for warning -- it's too
    // washed out to double as legible text).
    const alertColorFor = (severity) => {
        const c = palette[severity];
        return { main: c.main, lightBg: c.light, titled: (severity === "warning" ? c.contrastText : c.dark) };
    };
    // Shared shape for MuiAlert's standard/outlined variants; each still sets its own backgroundColor.
    const alertBaseStyle = (severity) => {
        const { main, titled } = alertColorFor(severity);
        return {
            color: main,
            border: `1px solid ${alpha(main, 0.5)}`,
            "& .MuiAlert-icon": {
                color: main,
                backgroundColor: alpha(main, 0.15),
                borderRadius: 6,
                padding: "4px",
                marginRight: 12,
            },
            // Titled alerts go neutral in the body -- only the icon + title read through `titled`.
            "& .MuiAlert-message:has(.MuiAlertTitle-root)": {
                color: palette.text.primary,
                "& .MuiAlertTitle-root": { color: titled },
            },
            "& .MuiAlert-icon:has(~ .MuiAlert-message .MuiAlertTitle-root)": { color: titled },
        };
    };
    // Maps an MUI color prop to a tone swatch (chips, tags, alerts).
    const toneFor = (color) => {
        switch (color) {
            case "primary":
            case "info": return tone.primary;
            case "success": return tone.success;
            case "warning": return tone.warning;
            case "error": return tone.error;
            case "slate": return tone.slate;
            case "secondary":
            case "default":
            case undefined: return tone.neutral;
            default: {
                // named accent colors (purple, cyan, teal, …): derive a soft tint from the palette
                const pc = palette[color];
                return pc ? { fg: pc.main, bg: alpha(pc.main, dark ? 0.22 : 0.15) } : tone.neutral;
            }
        }
    };
    return createTheme({
        palette: palette,
        spacing: 4,
        shape: { borderRadius: 8 },
        shadows: softShadows,
        transitions: {
            duration: {
                shortest: 130, shorter: 180, short: 220, standard: 280,
                complex: 360, enteringScreen: 220, leavingScreen: 180,
            },
        },
        typography: {
            fontFamily: `'${AppConfig.fonts.primary}', Helvetica, Arial, sans-serif`,
            fontWeightLight: 400,
            fontWeightRegular: 500,
            fontWeightMedium: 600,
            fontWeightBold: 800,
            fontSize: 12,
            h1: { fontWeight: 800, lineHeight: 1.15, letterSpacing: "-0.01em" },
            h2: { fontWeight: 800, lineHeight: 1.15, letterSpacing: "-0.01em" },
            h3: { fontWeight: 800, lineHeight: 1.2, letterSpacing: "-0.01em" },
            h4: { fontWeight: 800, lineHeight: 1.2, letterSpacing: "-0.005em" },
            h5: { fontWeight: 800, lineHeight: 1.25, letterSpacing: "-0.005em" },
            h6: { fontWeight: 700, lineHeight: 1.3, letterSpacing: "-0.005em" },
            subtitle1: { fontWeight: 700, lineHeight: 1.3, letterSpacing: "-0.005em" },
            subtitle2: { fontSize: "16px", fontWeight: 700, lineHeight: 1.3, letterSpacing: "-0.005em" },
            body1: { fontSize: "15px", fontWeight: 600, lineHeight: 1.4, letterSpacing: "-0.005em" },
            body2: { fontSize: "14px", fontWeight: 600, lineHeight: 1.45, letterSpacing: "-0.005em" },
            caption: { fontSize: "12px", fontWeight: 700, lineHeight: 1.3, letterSpacing: "-0.005em" },
            overline: { fontWeight: 700, lineHeight: 1, letterSpacing: "0.05em", textTransform: "uppercase" },
            button: { fontSize: "14px", fontWeight: 700, lineHeight: 1.6, letterSpacing: "-0.005em", textTransform: "none" },
        },
        components: {
            // ── Baseline: links + shared keyframes ──────────────────────────────────
            MuiCssBaseline: {
                styleOverrides: {
                    a: { color: palette.primary.main, textDecoration: "none", "&:hover": { textDecoration: "underline" } },
                    "@keyframes fadeIn": { from: { opacity: 0 }, to: { opacity: 1 } },
                    "@keyframes slideUp": { from: { opacity: 0, transform: "translateY(10px)" }, to: { opacity: 1, transform: "translateY(0)" } },
                    // notistack renders its own internal Snackbar/Content — it never touches MUI's
                    // <Snackbar> component, so `components.MuiSnackbar.styleOverrides` is never consumed.
                    // These have to live here (real global CSS injection) instead.
                    // Material toast instead of the app's pastel Alert styling.
                    ".notistack-MuiContent": { borderRadius: 8, fontWeight: 700 },
                    ".notistack-MuiContent-success": { backgroundColor: palette.success.dark, color: palette.common.white },
                    ".notistack-MuiContent-error": { backgroundColor: palette.error.main, color: palette.common.white },
                    ".notistack-MuiContent-warning": { backgroundColor: palette.warning.main, color: palette.common.white },
                    ".notistack-MuiContent-info": { backgroundColor: palette.info.main, color: palette.common.white },
                    ".notistack-MuiContent-default": { backgroundColor: palette.invBg, color: palette.invFg },
                },
            },
            MuiLink: {
                styleOverrides: {
                    root: { color: palette.primary.main, textDecoration: "none", fontWeight: 700, "&:hover": { textDecoration: "underline" } },
                },
            },
            // ── Data Grid (MUI X) ───────────────────────────────────────────────────
            // MUI X v7 stopped rendering cells as flex containers, so custom renderCell
            // content (chips, buttons, icon avatars) top-aligns in its row. Restore the
            // v6 vertical centering app-wide — MUI's own v7 migration recipe. Note:
            // grid/theme.tsx spreads these styleOverrides, so themed grids inherit it.
            MuiDataGrid: {
                styleOverrides: {
                    cell: { display: "flex", alignItems: "center" },
                },
            },
            // ── Buttons (kit: primary=contained, secondary=outlined, ghost=text) ─────
            MuiButton: {
                defaultProps: { disableElevation: true },
                styleOverrides: {
                    root: {
                        textTransform: "none",
                        borderRadius: 8,
                        padding: "5px 14px",
                        boxShadow: "none",
                        transition: "all 0.2s ease-in-out",
                        cursor: "pointer",
                        // "&:hover": { boxShadow: hoverShadowSm, transform: "translateY(-1px)" },
                        "&.Mui-disabled": { opacity: 0.5 },
                    },
                    sizeSmall: { fontSize: "12.7px", height: "29.6px" },
                    sizeMedium: { fontSize: "13px" },
                    sizeLarge: { fontSize: "14px", padding: "5px 16px" },
                    contained: {
                        color: palette.common.white,
                        "&:hover": { boxShadow: hoverShadowMd },
                    },
                    containedSecondary: {
                        color: palette.secondary.contrastText,
                        // Shave 1px per axis so the border doesn't make this taller/wider than its
                        // border-less contained siblings — same compensation the `outlined` variant uses.
                        padding: "4px 13px",
                        border: hairlineBorder,
                        "&:hover": { border: `1px solid ${palette.borderStrong}`, boxShadow: hoverShadowMd },
                    },
                    outlined: ({ ownerState }) => ({
                        padding: "4px 13px",
                        borderWidth: "1px",
                        "&:hover": {
                            borderWidth: "1px",
                            //transform: "translateY(-1px)",
                            backgroundColor: ownerState.color && ownerState.color !== "inherit" && palette[ownerState.color]
                                ? alpha(palette[ownerState.color].main, 0.1)
                                : palette.secondary.main,
                        },
                    }),
                    outlinedSecondary: {
                        color: palette.text.secondary,
                        border: `1px solid ${palette.borderStrong}`,
                        backgroundColor: palette.cardBg,
                        "&:hover": { backgroundColor: palette.secondary.main, boxShadow: hoverShadowMd },
                        "& .MuiButton-icon": { color: palette.text.tertiary },
                    },
                    text: ({ ownerState }) => ({
                        "&:hover": {
                            boxShadow: "none",
                            //transform: "translateY(-1px)",
                            backgroundColor: ownerState.color && ownerState.color !== "inherit" && palette[ownerState.color]
                                ? alpha(palette[ownerState.color].main, 0.1)
                                : palette.secondary.main,
                        },
                    }),
                    textSecondary: { color: palette.text.secondary, "&:hover": { backgroundColor: palette.secondary.main } },
                },
            },
            MuiIconButton: {
                styleOverrides: {
                    root: ({ ownerState }) => ({
                        borderRadius: 8,
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                            backgroundColor: ownerState.color && ownerState.color !== "inherit" && palette[ownerState.color]
                                ? alpha(palette[ownerState.color].main, 0.08)
                                : palette.secondary.main,
                        },
                    }),
                    colorSecondary: { color: palette.text.secondary, "&:hover": { backgroundColor: palette.secondary.main } },
                    colorError: { "&:hover": { backgroundColor: palette.error.light } },
                    colorWarning: { "&:hover": { backgroundColor: palette.warning.light } },
                    colorSuccess: { "&:hover": { backgroundColor: palette.success.light } },
                    colorInfo: { "&:hover": { backgroundColor: palette.info.light } },
                    sizeSmall: { fontSize: "14px !important" },
                },
            },
            // ── Chip (kit: soft tinted bg + saturated fg, no border, radius 6) ───────
            MuiChip: {
                styleOverrides: {
                    root: ({ ownerState }) => {
                        const c = toneFor(ownerState.color);
                        const filled = ownerState.variant !== "outlined";
                        // Default (uncolored) chips keep the neutral fill but use the primary text color.
                        const isDefault = !ownerState.color || ownerState.color === "default";
                        const fg = isDefault ? palette.text.primary : c.fg;
                        return {
                            borderRadius: 6,
                            height: 24,
                            padding: "0 9px",
                            gap: 4,
                            fontSize: 12,
                            fontWeight: 700,
                            lineHeight: 1,
                            letterSpacing: "0.01em",
                            backgroundColor: c.bg,
                            color: fg,
                            border: filled ? "none" : `1px solid ${alpha(c.fg, 0.35)}`,
                            "& .MuiChip-label": { padding: 0 },
                            "& .MuiChip-icon, & .MuiChip-deleteIcon, & .MuiSvgIcon-root": { color: c.fg, margin: 0, fontSize: 16 },
                        };
                    },
                    sizeSmall: {
                        height: 20, padding: "0 7px", fontSize: 11,
                        "& .MuiChip-icon, & .MuiChip-deleteIcon, & .MuiSvgIcon-root": { fontSize: 12 },
                    },
                },
            },
            // ── Segmented control (kit LevelSegmented) ───────────────────────────────
            MuiToggleButtonGroup: {
                styleOverrides: {
                    root: {
                        gap: 2,
                        padding: 2,
                        border: "none",
                        borderRadius: 8,
                        backgroundColor: dark ? "rgba(255,255,255,0.05)" : palette.secondary.main,
                    },
                },
            },
            MuiToggleButton: {
                styleOverrides: {
                    root: {
                        border: "none",
                        borderRadius: "6px !important",
                        padding: "4px 12px",
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: 12.5,
                        letterSpacing: "-0.005em",
                        color: palette.text.tertiary,
                        "&:hover": { backgroundColor: alpha(palette.cardBg, dark ? 0.35 : 0.5) },
                        "&.Mui-selected": {
                            backgroundColor: palette.cardBg,
                            color: palette.text.primary,
                            boxShadow: "0px 1px 3px rgba(0,0,0,0.2)",
                            "&:hover": { backgroundColor: palette.cardBg, color: palette.text.primary },
                        },
                    },
                    sizeSmall: { padding: "3px 10px", fontSize: 11.5 },
                    sizeLarge: { padding: "7px 14px" },
                },
            },
            // ── Inputs (kit SearchInput: radius 8, neutral border, primary focus halo) ─
            MuiInputBase: {
                styleOverrides: {
                    root: {
                        fontSize: "14px !important", fontWeight: 600,
                        "& .MuiInputAdornment-root": { marginRight: 0 },
                    },
                }
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        borderRadius: 8,
                        backgroundColor: palette.input,
                        transition: "all 0.13s ease-in-out",
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: palette.borderStrong },
                        "&:hover:not(.Mui-error) .MuiOutlinedInput-notchedOutline": { borderColor: palette.primary.main, borderWidth: "1.5px" },
                        // Only widen on focus — leave the color to MUI so error fields stay red, not blue.
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderWidth: "2px" },
                    },
                    input: { padding: "7.5px 14px", "&::placeholder": { color: palette.text.tertiary, opacity: 1 } },
                },
            },
            MuiAutocomplete: {
                styleOverrides: {
                    // Autocomplete's own inputRoot carries its own vertical padding on top of the inner
                    // input's — stacked, that renders roughly twice as tall as a plain OutlinedInput. Keep
                    // the TOTAL vertical padding at 7.5px/side (matching the app's other fields) but split
                    // it 4px on the root + 3.5px on the input. The split is height-neutral for a single-line
                    // Autocomplete, yet gives wrapped multi-select chips an even top/row/bottom rhythm: the
                    // 4px root padding sets the top/bottom edge gap, `gap: 4` sets the inter-chip and
                    // inter-row gap, and `alignContent: flex-start` stops wrapped rows from spreading apart.
                    // The tag margin is zeroed so only `gap` controls chip spacing.
                    // The nested `input` selector (not a top-level `input` styleOverride) is required — MUI
                    // bakes the input's default padding into a same-specificity rule scoped under inputRoot,
                    // so a plain `input` override loses; matching that nesting is what lets ours win.
                    inputRoot: {
                        paddingTop: 4,
                        paddingBottom: 4,
                        gap: 4,
                        alignContent: "flex-start",
                        "& .MuiAutocomplete-input": { padding: "3.5px 4px" },
                    },
                    tag: {
                        margin: 0,
                    },
                },
            },
            MuiTextField: {
                styleOverrides: {
                    root: {
                        // Padding lives on the input container (not the <input>) so start/end adornments
                        // (search icon, picker button) sit inside the field instead of flush at the edges.
                        "& .MuiFormLabel-root.MuiInputLabel-outlined": { top: "-6px" },
                        "& .MuiFormLabel-root.MuiInputLabel-outlined.MuiInputLabel-shrink": { top: "1px" },
                    },
                },
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        color: palette.text.secondary,
                        fontWeight: 600,
                        fontSize: "13px !important",
                        // top rules vertically center the resting (placeholder) label — notably for
                        // standalone <Select>, which isn't wrapped in a TextField.
                        top: "4px",
                        "&.MuiInputLabel-outlined.MuiInputLabel-sizeSmall": { top: "0px" },
                        "&.MuiInputLabel-outlined": { top: "-6px" },
                        "&.MuiInputLabel-outlined.MuiInputLabel-shrink": { top: "2px" },
                        "&.MuiInputLabel-shrink": { fontSize: "13px !important" },
                    },
                },
            },
            MuiSelect: { styleOverrides: { select: { borderRadius: 8 } } },
            MuiCheckbox: { styleOverrides: { root: { padding: 4 } } },
            MuiRadio: { styleOverrides: { root: { padding: 4 } } },
            MuiFormControlLabel: {
                styleOverrides: { root: { marginLeft: "unset" }, label: { fontSize: 14, fontWeight: 600 } },
            },
            MuiSwitch: {
                styleOverrides: {
                    root: {
                        width: 36, height: 20, padding: 0, borderRadius: 20, marginRight: 6,
                        "& .MuiSwitch-switchBase": {
                            padding: 0, margin: "2px 0 0 2px", transitionDuration: "300ms",
                            "&.Mui-checked": {
                                transform: "translateX(16px)", color: tw.white,
                                "& + .MuiSwitch-track": { backgroundColor: palette.primary.main, opacity: 1, border: 0 },
                            },
                        },
                        "& .MuiSwitch-thumb": { boxSizing: "border-box", width: 16, height: 16, boxShadow: "0 1px 2px rgba(0,0,0,0.3)" },
                        "& .MuiSwitch-track": { backgroundColor: palette.text.tertiary, opacity: dark ? 0.3 : 1, transition: "background-color 300ms" },
                        // Keep the disabled thumb white (just dimmed) — otherwise a disabled+on switch keeps the blue checked color.
                        "& .MuiSwitch-switchBase.Mui-disabled .MuiSwitch-thumb": { color: tw.white, opacity: dark ? "0.5 !important" : "0.75 !important" },
                        "& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track": { opacity: dark ? "0.2 !important" : "0.4 !important" },
                    },
                    sizeSmall: {
                        width: 28, height: 16,
                        "& .MuiSwitch-thumb": { width: 12, height: 12 },
                        "& .MuiSwitch-switchBase.Mui-checked": { transform: "translateX(12px)" },
                    },
                },
            },
            // ── Surfaces ─────────────────────────────────────────────────────────────
            MuiPaper: {
                defaultProps: { elevation: 0 },
                styleOverrides: {
                    root: { backgroundColor: palette.cardBg, backgroundImage: "none", borderRadius: 12, border: hairlineBorder },
                    elevation1: { boxShadow: softShadows[2] },
                    elevation2: { boxShadow: softShadows[3] },
                    elevation3: { boxShadow: softShadows[4] },
                },
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        borderRadius: 12,
                        backgroundColor: palette.cardBg,
                        border: hairlineBorder,
                        boxShadow: softShadows[2],
                        transition: "box-shadow 0.2s ease-in-out",
                        "&:hover": { boxShadow: softShadows[4] },
                        "& .MuiCardHeader-subheader": { marginTop: 2, fontSize: 14 },
                    },
                },
            },
            MuiCardContent: { styleOverrides: { root: { "&:last-child": { paddingBottom: 16 } } } },
            MuiDivider: { styleOverrides: { root: { borderColor: palette.border, margin: "2px 0" } } },
            // ── Menu / popover ───────────────────────────────────────────────────────
            MuiMenu: {
                styleOverrides: {
                    paper: { backgroundColor: palette.raised, borderRadius: 8, marginTop: 4, border: hairlineBorder, boxShadow: softShadows[7] },
                    list: { display: "flex", flexDirection: "column", gap: 2, padding: "5px !important" },
                },
            },
            MuiMenuItem: {
                styleOverrides: {
                    // Let options inherit the default body typography + padding (as before) rather than
                    // the smaller/tighter custom text the refactor introduced.
                    root: {
                        borderRadius: 6, minHeight: 34,
                        "&:hover": { backgroundColor: palette.rowHover },
                        "&.Mui-selected": { backgroundColor: palette.primary.light },
                    },
                },
            },
            MuiPopover: {
                styleOverrides: {
                    paper: { backgroundColor: palette.raised, borderRadius: 8, border: hairlineBorder, boxShadow: softShadows[7] },
                },
            },
            MuiListSubheader: {
                styleOverrides: {
                    root: {
                        backgroundColor: "inherit", fontWeight: 800, fontSize: 10.5, letterSpacing: "0.06em",
                        textTransform: "uppercase", color: palette.text.tertiary,
                    },
                },
            },
            MuiListItemAvatar: { styleOverrides: { root: { minWidth: 36 } } },
            // ── Tooltip ──────────────────────────────────────────────────────────────
            MuiTooltip: {
                styleOverrides: {
                    tooltip: {
                        fontSize: "12px",
                        lineHeight: 1.3,
                        fontWeight: 700,
                        backgroundColor: palette.text.primary,
                        color: palette.cardBg,
                        borderRadius: 8,
                        padding: "6px 12px",
                        boxShadow: "0px 4px 12px rgba(0,0,0,0.15)",
                        backdropFilter: "blur(4px)",
                    },
                    arrow: { color: palette.text.primary },
                },
            },
            // ── Dialog (kit Modal: radius 16, soft overlay) ──────────────────────────
            MuiDialog: {
                defaultProps: { scroll: "paper" },
                styleOverrides: { paper: { backgroundColor: palette.cardBg, borderRadius: 16, border: hairlineBorder, boxShadow: "0px 16px 48px rgba(0,0,0,0.35)" } },
            },
            MuiDialogTitle: { styleOverrides: { root: { fontWeight: 800, fontSize: 18, letterSpacing: "-0.01em", padding: "18px 22px 14px" } } },
            MuiDialogContent: { styleOverrides: { root: { padding: "22px" } } },
            MuiDialogActions: {
                styleOverrides: {
                    root: {
                        padding: "10px 16px",
                        borderTop: hairlineBorder,
                        backgroundColor: palette.pageBg,
                        // First (leftmost) action = neutral/secondary button; last = primary CTA.
                        "& > .MuiButton-root:first-of-type:not(:last-of-type)": {
                            backgroundColor: dark ? palette.secondary.main : palette.common.white,
                            color: palette.secondary.contrastText,
                            // Shave 1px per axis so the border doesn't make this taller/wider than the
                            // border-less last-of-type CTA button — same compensation `outlined` uses.
                            padding: "4px 13px",
                            border: hairlineBorder,
                            borderRadius: 8,
                            transition: "all 0.2s ease-in-out",
                            "&:hover": {
                                textDecoration: "none",
                                backgroundColor: dark ? palette.secondary.dark : palette.secondary.main,
                                border: `1px solid ${palette.borderStrong}`,
                                boxShadow: hoverShadowSm,
                                //transform: "translateY(-1px)",
                            },
                            "&.Mui-disabled": { opacity: 0.5, border: "none" },
                        },
                        "& > .MuiButton-root:last-of-type:not(:first-of-type)": {
                            backgroundColor: palette.primary.main,
                            color: palette.common.white,
                            borderRadius: 8,
                            transition: "all 0.2s ease-in-out",
                            "&:hover": {
                                textDecoration: "none",
                                backgroundColor: palette.primary.dark,
                                boxShadow: hoverShadowMd,
                                //transform: "translateY(-1px)",
                            },
                            "&.Mui-disabled": { opacity: 0.5, border: "none" },
                        },
                    },
                },
            },
            // ── Alert (modeled on BoxFormAlertBar: tinted icon box, colored title, neutral body) ──
            MuiAlert: {
                styleOverrides: {
                    root: {
                        borderRadius: 8, padding: "0px 12px", lineHeight: 1.6, fontWeight: 700, alignItems: "center",
                        "& .MuiAlert-action": { paddingTop: 0, alignItems: "center" },
                        // Only titled alerts pad their message block; plain alerts stay compact.
                        "& .MuiAlert-message:has(.MuiAlertTitle-root)": { padding: "11px 0 6px 0" },
                        "& .MuiAlertTitle-root": { marginBottom: -2, fontWeight: 800, fontSize: 12, letterSpacing: "0.05em", textTransform: "uppercase" },
                    },
                    standard: ({ ownerState }) => {
                        var _a, _b;
                        const severity = ((_b = (_a = ownerState.color) !== null && _a !== void 0 ? _a : ownerState.severity) !== null && _b !== void 0 ? _b : "success");
                        const { lightBg, main } = alertColorFor(severity);
                        return Object.assign(Object.assign({}, alertBaseStyle(severity)), { 
                            // lightBg is a translucent alpha() tint, so a plain backgroundColor lets whatever
                            // sits behind the alert bleed through and shift its color. Paint the tint as a
                            // flat-color background-image layer over a solid cardBg base instead, so the
                            // alert's own background is always opaque regardless of its surroundings
                            // (BoxFormAlertBar gets this for free from its own opaque cardBg wrapper Box).
                            backgroundColor: palette.cardBg, backgroundImage: `linear-gradient(${lightBg}, ${lightBg})`, "& .MuiAlert-action .MuiButtonBase-root:not(.MuiButton-contained)": { color: main, "&:hover": { backgroundColor: alpha(main, 0.12) } } });
                    },
                    outlined: ({ ownerState }) => {
                        var _a, _b;
                        const severity = ((_b = (_a = ownerState.color) !== null && _a !== void 0 ? _a : ownerState.severity) !== null && _b !== void 0 ? _b : "success");
                        return Object.assign(Object.assign({}, alertBaseStyle(severity)), { backgroundColor: "transparent" });
                    },
                },
            },
            // ── Badge / avatar ───────────────────────────────────────────────────────
            MuiBadge: {
                styleOverrides: {
                    badge: { fontWeight: 800, lineHeight: 1, color: palette.common.white, height: 16, minWidth: 16, padding: 5 },
                    colorSecondary: { color: `${palette.secondary.contrastText} !important` },
                },
            },
            MuiAvatar: { styleOverrides: { root: { border: `2px solid ${palette.cardBg}`, backgroundColor: palette.cardBgAlt, color: "inherit", fontWeight: 700 } } },
            MuiAvatarGroup: { styleOverrides: { avatar: { border: `2px solid ${palette.cardBg}`, backgroundColor: palette.cardBgAlt, color: "inherit" } } },
            // ── Table (kit matrix: tinted uppercase head, hairline rows, hover) ──────
            MuiTable: {
                styleOverrides: {
                    root: {
                        borderCollapse: "separate",
                        borderSpacing: 0,
                        backgroundColor: palette.cardBg,
                        border: hairlineBorder,
                        borderRadius: 12,
                        boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.08)",
                        overflow: "hidden",
                    },
                },
            },
            MuiTableHead: {
                styleOverrides: {
                    root: {
                        "& .MuiTableCell-head": {
                            backgroundColor: palette.headerTint,
                            borderBottom: hairlineBorder,
                            fontSize: 12, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase",
                            color: palette.text.secondary, paddingTop: 10, paddingBottom: 10,
                        },
                    },
                },
            },
            MuiTableRow: {
                styleOverrides: {
                    root: { transition: "background-color 0.15s ease-in-out", "&:hover": { backgroundColor: palette.rowHover } },
                },
            },
            MuiTableCell: {
                styleOverrides: {
                    root: {
                        padding: "14px 16px", borderBottom: hairlineBorder,
                        "&:first-of-type": { paddingLeft: 32 },
                        "&:last-of-type": { paddingRight: 32 },
                        "tbody tr:last-child &": { borderBottom: "none" },
                    },
                },
            },
            // ── Accordion ────────────────────────────────────────────────────────────
            MuiAccordion: {
                styleOverrides: {
                    root: {
                        paddingLeft: 12, paddingRight: 12, borderRadius: "12px !important",
                        "&::before": { display: "none" },
                        "&.Mui-expanded": { margin: "unset" },
                        "& .MuiAccordionSummary-root": { minHeight: "unset" },
                        "& .MuiAccordionSummary-root.Mui-expanded": { minHeight: "unset" },
                        "& .MuiAccordionSummary-content.Mui-expanded": { margin: "12px 0" },
                    },
                },
            },
            // ── Nav / layout ─────────────────────────────────────────────────────────
            MuiDrawer: {
                styleOverrides: {
                    paper: { zIndex: 1000, backgroundColor: palette.cardBg, borderColor: palette.border, "& .MuiListItem-root": { paddingTop: 0, paddingBottom: 0 } },
                },
            },
            MuiContainer: {
                styleOverrides: { maxWidthLg: { maxWidth: "1280px !important" }, maxWidthMd: { maxWidth: "960px !important" } },
            },
            // ── Stepper ──────────────────────────────────────────────────────────────
            MuiStepper: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        marginTop: 8, marginBottom: 20,
                        "& .MuiStepLabel-label": {
                            color: palette.text.primary, fontSize: "0.875rem", fontWeight: 500,
                            [theme.breakpoints.down("sm")]: { display: "none" },
                            "&.Mui-active": { fontWeight: 700 },
                        },
                        "& .MuiStepIcon-root": {
                            color: palette.text.tertiary,
                            "& .MuiStepIcon-text": { fill: palette.common.white, fontSize: ".9rem" },
                        },
                        "& .MuiStepIcon-root.Mui-active": { color: palette.primary.main, transform: "scale(1.15)" },
                        "& .MuiStepIcon-root.Mui-completed": { color: palette.primary.main },
                        "& .MuiStepConnector-line": { borderColor: palette.borderStrong },
                    }),
                },
            },
            // ── Date & time pickers (kept from the app; re-pointed to the new tokens) ──
            MuiPickersPopper: {
                styleOverrides: { paper: { backgroundColor: palette.cardBg, border: hairlineBorder, borderRadius: 12, boxShadow: "0px 8px 24px rgba(0,0,0,0.18)", overflow: "hidden" } },
            },
            MuiPickersLayout: {
                styleOverrides: {
                    actionBar: {
                        backgroundColor: palette.cardBg, borderTop: hairlineBorder, padding: "8px 12px", display: "flex", gap: 8,
                        "& .MuiButton-root": { fontSize: 12.5, fontWeight: 700, textTransform: "none" }
                    },
                },
            },
            MuiDateCalendar: { styleOverrides: { root: { height: "unset", "& .MuiPickersDay-root": { fontSize: 14 } } } },
            MuiPickersDay: {
                styleOverrides: {
                    root: {
                        fontSize: 14,
                        "&:hover": { backgroundColor: `${palette.secondary.main} !important` },
                        "&.MuiPickersDay-today": { backgroundColor: `${palette.secondary.main} !important`, border: `1px solid ${palette.secondary.dark} !important` },
                        "&.Mui-selected": Object.assign(Object.assign({}, selectedPickerBase), { "&:hover": { backgroundColor: `${palette.primary.main} !important` } }),
                    },
                },
            },
            MuiPickersMonth: {
                styleOverrides: {
                    monthButton: {
                        "&:hover": { backgroundColor: `${palette.secondary.main} !important` },
                        "&.Mui-selected": selectedPickerBase,
                    },
                },
            },
            MuiPickersYear: {
                styleOverrides: {
                    yearButton: {
                        "&:hover": { backgroundColor: `${palette.secondary.main} !important` },
                        "&.Mui-selected": selectedPickerBase,
                    },
                },
            },
            MuiTimeClock: { styleOverrides: { root: { backgroundColor: palette.cardBg } } },
            MuiClock: { styleOverrides: { wrapper: { backgroundColor: palette.cardBgAlt }, pin: { backgroundColor: palette.primary.main } } },
            MuiClockPointer: { styleOverrides: { root: { backgroundColor: palette.primary.main }, thumb: { backgroundColor: palette.primary.main, borderColor: palette.primary.main } } },
            MuiClockNumber: { styleOverrides: { root: { fontSize: 13, fontWeight: 600, color: palette.text.secondary, "&.Mui-selected": { color: `${palette.common.white} !important` } } } },
            MuiMultiSectionDigitalClock: { styleOverrides: { root: { backgroundColor: palette.cardBg, border: "none", padding: "4px 0", "& .MuiDivider-root": { borderColor: palette.border } } } },
            MuiMultiSectionDigitalClockSection: {
                styleOverrides: {
                    root: {
                        width: 60, scrollbarWidth: "none", "&::after": { display: "none !important" },
                        "& .MuiMenuItem-root": {
                            fontSize: 13, fontWeight: 600, borderRadius: 6, margin: "2px 4px", minHeight: 30, height: 30,
                            display: "flex", justifyContent: "center", alignItems: "center", color: palette.text.secondary,
                            "&:hover": { backgroundColor: palette.secondary.main, color: palette.text.primary },
                            "&.Mui-selected": Object.assign(Object.assign({}, selectedPickerBase), { fontWeight: 700, "&:hover": { backgroundColor: `${palette.primary.dark} !important` } }),
                        },
                    },
                },
            },
        },
    });
};
// Pre-create both modes.
const lightTheme = createThemeWithComponents("light");
const darkTheme = createThemeWithComponents("dark");
const createAppTheme = (mode) => (mode === "light" ? lightTheme : darkTheme);
export { createAppTheme, lightTheme, darkTheme };
export default lightTheme;
/* eslint-enable prettier/prettier */
